import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, pollJob, jobErrorMessage, newIdemKey, idemHeaders } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Loader2, ArrowLeft, Sparkles, Play, Pause, CheckCircle2, Archive, RefreshCw, XCircle, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { CAMPAIGN_STATUS_STYLES, CONTENT_STATUS_STYLES } from "./constants";
import CreativeBrief from "./CreativeBrief";

function Field({ label, value }) {
  return (<div><p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{label}</p><p className="text-sm text-gray-800 mt-1 whitespace-pre-line">{value || "—"}</p></div>);
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [camp, setCamp] = useState(null);
  const [content, setContent] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);

  const load = () => Promise.all([
    api.get(`/marketing/campaigns/${id}`),
    api.get(`/marketing/content?campaign_id=${id}`),
    api.get(`/marketing/jobs?campaign_id=${id}`),
  ]).then(([c, ct, j]) => {
    setCamp(c.data); setContent(ct.data); setJobs(j.data);
    const running = j.data.find((x) => x.status === "Queued" || x.status === "Running");
    setActiveJob(running || null);
  }).catch(() => setCamp(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Persistent job visibility: poll any active job (survives refresh / navigation back).
  useEffect(() => {
    if (!activeJob) return;
    let cancelled = false;
    pollJob(activeJob.id, { onUpdate: (j) => { if (!cancelled) setActiveJob(j); } })
      .then((j) => { if (cancelled) return; if (j.status === "Failed") toast.error(jobErrorMessage(j)); load(); })
      .catch(() => {});
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [activeJob?.id]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); } };
  const setStatus = (status) => act(() => api.put(`/marketing/campaigns/${id}`, { status }), `Campaign ${status}`);

  const runJob = async (postFn, startMsg) => {
    setBusy(true);
    try {
      const { data } = await postFn();
      toast.message(startMsg);
      setActiveJob(data.job || { id: data.job_id, status: data.status });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const generate = () => runJob(
    () => api.post(`/marketing/campaigns/${id}/generate-batch?batch_size=7`, null, idemHeaders(newIdemKey())),
    "Generation started — running in the background",
  );
  const regenStrategy = () => setRegenOpen(true);
  const doRegenStrategy = () => {
    setRegenOpen(false);
    runJob(() => api.post(`/marketing/campaigns/${id}/regenerate-strategy`, {
      offer_id: camp.offer_id, goal_text: camp.objective || "Enrollment", goal_number: camp.target_goal,
      length_days: camp.length_days || 30, platforms: camp.platforms || [], approval_preference: camp.approval_preference || "Weekly Batch Review",
    }, idemHeaders(newIdemKey())), "Strategy regeneration started");
  };
  const cancelJob = (jid) => act(() => api.post(`/marketing/jobs/${jid}/cancel`), "Cancellation requested");
  const retryJob = (jid) => runJob(() => api.post(`/marketing/jobs/${jid}/retry`), "Retry started");
  const toggleQueue = (enabled) => act(() => api.put(`/marketing/campaigns/${id}/maintain-queue`, { enabled }), enabled ? "Automation on" : "Automation off");

  if (camp === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (camp === false) return <p className="text-sm text-red-600">Campaign not found.</p>;

  const s = camp.strategy || {};
  const counts = content.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; }, {});

  return (
    <div data-testid="campaign-detail">
      <button onClick={() => navigate("/marketing/campaigns")} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900" data-testid="campaign-back"><ArrowLeft className="h-4 w-4" /> Back to Campaigns</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mt-4">
        <div>
          <h2 className="font-heading text-2xl font-bold">{camp.name}</h2>
          <div className="flex items-center gap-2 mt-2">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${CAMPAIGN_STATUS_STYLES[camp.status] || "bg-gray-100 text-gray-600"}`}>{camp.status}</span>
            <span className="text-xs text-gray-500">Approval: {camp.approval_preference || "—"} · {camp.length_days || "?"}-day</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={generate} disabled={busy || !!activeJob} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="generate-batch-button"><Sparkles className="h-4 w-4 mr-1.5" /> {content.length === 0 ? "Generate First Batch" : "Generate Next 7 Days"}</Button>
          <Button onClick={regenStrategy} disabled={busy || !!activeJob} variant="outline" className="border-gray-200" data-testid="regenerate-strategy-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Regenerate Strategy</Button>
          {camp.status === "Active"
            ? <Button onClick={() => setStatus("Paused")} disabled={busy} variant="outline" className="border-gray-200" data-testid="pause-button"><Pause className="h-4 w-4 mr-1.5" /> Pause</Button>
            : <Button onClick={() => setStatus("Active")} disabled={busy} variant="outline" className="border-gray-200" data-testid="resume-button"><Play className="h-4 w-4 mr-1.5" /> Resume</Button>}
          <Button onClick={() => setStatus("Completed")} disabled={busy} variant="outline" className="border-gray-200" data-testid="complete-button"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Complete</Button>
          <Button onClick={() => setStatus("Archived")} disabled={busy} variant="outline" className="border-gray-200 hover:text-amber-600 hover:border-amber-500" data-testid="archive-campaign-button"><Archive className="h-4 w-4 mr-1.5" /> Archive</Button>
        </div>
      </div>

      {activeJob && (
        <div className="mt-4 flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50 px-4 py-3" data-testid="active-job-banner">
          <div className="flex items-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin text-[#581C87]" />
            <div>
              <p className="text-sm font-medium text-[#581C87]">AI job {activeJob.status?.toLowerCase()} — {activeJob.requested_output || activeJob.job_type}</p>
              <p className="text-xs text-purple-500">This runs in the background. You can leave this page; it keeps going.</p>
            </div>
          </div>
          <Button onClick={() => cancelJob(activeJob.id)} disabled={busy} variant="outline" size="sm" className="border-purple-200 text-purple-700 hover:text-red-600 hover:border-red-400" data-testid="cancel-job-button"><XCircle className="h-4 w-4 mr-1.5" /> Cancel</Button>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 max-w-md" data-testid="maintain-queue-row">
        <div>
          <p className="text-sm font-medium">Maintain Weekly Approval Queue</p>
          <p className="text-xs text-gray-500">Auto-create the next 7-day batch (never publishes). Off until strategy approved.</p>
        </div>
        <Switch checked={!!camp.maintain_weekly_queue} onCheckedChange={toggleQueue} data-testid="maintain-queue-switch" />
      </div>

      {/* Content counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-6">
        {["Needs Review", "Approved", "Rejected", "Draft", "Archived", "Generating"].map((st) => (
          <div key={st} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`count-${st.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="font-heading text-xl font-bold">{counts[st] || 0}</p>
            <span className={`inline-flex mt-1 rounded-full px-2 py-0.5 text-xs ${CONTENT_STATUS_STYLES[st] || "bg-gray-100 text-gray-600"}`}>{st}</span>
          </div>
        ))}
      </div>

      {/* Strategy */}
      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="campaign-strategy">
        <Field label="Goal" value={camp.objective} />
        <Field label="Selected Offer ID" value={camp.offer_id} />
        <Field label="Audience" value={s.primary_audience_segment || camp.target_audience} />
        <Field label="Key Message" value={s.key_message} />
        <Field label="Positioning / Strategy" value={s.positioning || camp.summary} />
        <Field label="Ethical Promise" value={s.ethical_promise} />
        <Field label="Content Pillars" value={(s.content_pillars || []).join("\n")} />
        <Field label="Weekly Themes" value={(s.weekly_themes || []).join("\n")} />
        <Field label="Recommended Platform Mix" value={(s.platform_mix || camp.platforms || []).join(", ")} />
        <Field label="Content Ratio" value={s.content_ratio ? Object.entries(s.content_ratio).map(([k, v]) => `${k}: ${v}`).join("  ") : "—"} />
        <Field label="CTA Strategy" value={s.cta_strategy} />
        <Field label="CTA Keyword" value={s.cta_keyword || camp.cta_keyword} />
        <Field label="Recommended Cadence" value={s.recommended_cadence} />
        <Field label="Landing-page Usage" value={s.landing_page_usage} />
        <Field label="Compliance Risks" value={(s.compliance_risks || []).join("\n")} />
        <Field label="Prohibited Approaches" value={(s.prohibited_approaches || []).join("\n")} />
      </div>

      <CreativeBrief campaignId={id} />

      {/* Generation history */}
      <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="generation-history">
        <div className="px-6 py-3 border-b border-gray-100"><h3 className="font-heading font-bold">Generation History</h3></div>
        {jobs.length === 0 ? <div className="px-6 py-6 text-center text-gray-400 text-sm">No generation jobs yet.</div>
          : jobs.map((j) => (
            <div key={j.id} className="px-6 py-3 border-b border-gray-50 flex items-center justify-between text-sm gap-3" data-testid={`job-${j.id}`}>
              <span className="text-gray-600">{j.requested_at?.slice(0, 16).replace("T", " ")} · {j.model}</span>
              <span className="text-gray-500 flex-1 text-right">{j.core_concepts} concepts · {j.platform_variants} posts · pass {j.items_passed}/warn {j.items_warned}/block {j.items_blocked}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs ${j.status === "Completed" ? "bg-emerald-100 text-emerald-700" : j.status === "Partially Completed" ? "bg-amber-100 text-amber-700" : j.status === "Failed" ? "bg-red-100 text-red-700" : j.status === "Cancelled" ? "bg-gray-100 text-gray-500" : "bg-slate-100 text-slate-600"}`}>{j.status}</span>
              {(j.status === "Failed" || j.status === "Partially Completed") && (
                <Button onClick={() => retryJob(j.id)} disabled={busy || !!activeJob} variant="ghost" size="sm" className="text-[#581C87]" data-testid={`retry-job-${j.id}`}><RotateCw className="h-3.5 w-3.5 mr-1" /> {j.status === "Partially Completed" ? "Finish batch" : "Retry"}</Button>
              )}
            </div>
          ))}
      </div>

      {/* Linked content */}
      <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="linked-content">
        <div className="px-6 py-3 border-b border-gray-100"><h3 className="font-heading font-bold">Linked Content ({content.length})</h3></div>
        {content.slice(0, 40).map((c) => (
          <button key={c.id} onClick={() => navigate(`/marketing/content/${c.id}`)} className="w-full text-left flex items-center justify-between px-6 py-2.5 border-b border-gray-50 hover:bg-gray-50">
            <span className="text-sm text-gray-800 truncate">{c.title} · {c.platform}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs ${CONTENT_STATUS_STYLES[c.status] || "bg-gray-100 text-gray-600"}`}>{c.status}</span>
          </button>
        ))}
        {content.length === 0 && <div className="px-6 py-6 text-center text-gray-400 text-sm">No content yet.</div>}
      </div>

      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="max-w-md" data-testid="regen-strategy-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Regenerate Strategy</DialogTitle>
            <DialogDescription>The current strategy is preserved in version history. A new strategy will be generated and become the active version.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRegenOpen(false)}>Cancel</Button>
            <Button onClick={doRegenStrategy} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="confirm-regen-strategy-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Regenerate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
